import { useAsyncFn } from 'react-use';

import { logger } from '@service-initiation/shared-clients-osp-logger';
import { useAsyncAtom } from '@service-initiation/shared-utils-jotai';
import {
  selectedAppointmentAsyncAtom,
  useCompanyIdAtom,
  useCustomerDetailsValue,
  useDeviceDetailsAsyncAtom,
  useLeadIdAtom,
  useMailInAtom,
  useRepairMessageAtom,
  useSelectedRepairsAsyncAtom,
  useStoreIdValue,
} from '@service-initiation/tech-care-data-access-store';
import { createMailIn } from '@service-initiation/tech-care-data-access-tech-care-services';

import { appendCustomerMessage, buildExtraDataForLog } from '../utils';

export const useCreateMailInLead = () => {
  const { value: deviceDetails } = useDeviceDetailsAsyncAtom();

  const { value: appointment } = useAsyncAtom(selectedAppointmentAsyncAtom);
  const customer = useCustomerDetailsValue();
  const { value: selectedRepairs } = useSelectedRepairsAsyncAtom();
  const [companyId] = useCompanyIdAtom();
  const [repairMessage] = useRepairMessageAtom();
  const [, setLeadId] = useLeadIdAtom();
  const [mailIn, setMailIn] = useMailInAtom();
  const storeId = useStoreIdValue();

  return useAsyncFn(async () => {
    if (
      !customer?.firstName ||
      !customer?.lastName ||
      !customer?.email ||
      !customer?.phoneNumber ||
      !deviceDetails?.id ||
      (!selectedRepairs && !companyId) ||
      !mailIn?.shippingAddress
    ) {
      const err = new Error(
        'missing userSelection props when trying to createMailIn'
      );
      const extraData = await buildExtraDataForLog();
      logger.error(err, extraData);
      throw err;
    }
    if (!storeId) {
      const err = new Error('missing store id when trying to createMailIn');
      logger.error(err);
      throw err;
    }

    const createMailInResult = await createMailIn({
      appointmentDateAndTimeUTC: undefined,
      companyId,
      customerDetails: {
        addressLine1: mailIn.shippingAddress.streetAddress,
        addressLine2: mailIn.shippingAddress.address2,
        city: mailIn.shippingAddress.city,
        email: customer?.email,
        firstName: customer?.firstName,
        lastName: customer?.lastName,
        phoneNumber: customer?.phoneNumber,
        postalCode: mailIn.shippingAddress.zip,
        state: mailIn.shippingAddress.state,
      },
      customerMessage: appendCustomerMessage(repairMessage, selectedRepairs),
      deviceRepairId:
        selectedRepairs && selectedRepairs.length > 0
          ? selectedRepairs[0].id
          : undefined,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      deviceTypeId: deviceDetails!.id,
      intentId: appointment?.intentId,
      storeId: storeId,
      subscribeToMarketingEmails: customer.agreeToEmailOffers ?? false,
    });

    if (createMailInResult.error) {
      const err = new Error(createMailInResult.error);
      const extraData = await buildExtraDataForLog();
      logger.error(err, extraData);
      throw err;
    }

    setLeadId(createMailInResult.leadId);
    setMailIn({
      ...mailIn,
      shippingLabel: createMailInResult.shippingLabel,
    });
  }, []);
};
